import {
  AddressAutocomplete,
  type AddressResult,
} from '@agentic-flow-studio/shared/components/job-plan/AddressAutocomplete';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import { ArrowLeft, Building, Loader2, Trash2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface LocationFormPageProps {
  mode: 'create' | 'edit';
}

export default function LocationFormPage({ mode }: LocationFormPageProps) {
  const { orgId, workspaceId, locationId } = useParams<{
    orgId: string;
    workspaceId: string;
    locationId?: string;
  }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const basePath = `/org/${orgId}/workspace/${workspaceId}/locations`;
  const numLocId = locationId ? parseInt(locationId, 10) : 0;

  // Form states
  const [label, setLabel] = useState('');
  const [formattedAddress, setFormattedAddress] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [accessNotes, setAccessNotes] = useState('');

  const { data: existingLoc, isLoading: isLocLoading } = trpc.jobPlan.locations.getById.useQuery(
    { id: numLocId },
    { enabled: mode === 'edit' && numLocId > 0 }
  );

  useEffect(() => {
    if (mode === 'edit' && existingLoc) {
      setLabel(existingLoc.label);
      setFormattedAddress(existingLoc.formattedAddress);
      setLatitude(existingLoc.latitude ?? null);
      setLongitude(existingLoc.longitude ?? null);
      setGooglePlaceId(existingLoc.googlePlaceId ?? null);
      setAccessNotes(existingLoc.accessNotes || '');
    }
  }, [mode, existingLoc]);

  const createLocMutation = trpc.jobPlan.locations.create.useMutation({
    onSuccess: () => {
      toast.success('Location created');
      utils.jobPlan.locations.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateLocMutation = trpc.jobPlan.locations.update.useMutation({
    onSuccess: () => {
      toast.success('Location updated');
      utils.jobPlan.locations.list.invalidate();
      utils.jobPlan.locations.getById.invalidate({ id: numLocId });
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteLocMutation = trpc.jobPlan.locations.delete.useMutation({
    onSuccess: () => {
      toast.success('Location deleted');
      utils.jobPlan.locations.list.invalidate();
      navigate(basePath);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSelectAddress = (result: AddressResult) => {
    setFormattedAddress(result.formattedAddress);
    setLatitude(result.latitude);
    setLongitude(result.longitude);
    setGooglePlaceId(result.googlePlaceId);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !formattedAddress.trim()) {
      toast.error('Please provide label and address');
      return;
    }

    if (mode === 'create') {
      createLocMutation.mutate({
        label: label.trim(),
        formattedAddress: formattedAddress.trim(),
        latitude,
        longitude,
        googlePlaceId,
        accessNotes: accessNotes.trim() || undefined,
      });
    } else {
      if (!numLocId) return;
      updateLocMutation.mutate({
        id: numLocId,
        label: label.trim(),
        formattedAddress: formattedAddress.trim(),
        latitude,
        longitude,
        googlePlaceId,
        accessNotes: accessNotes.trim() || undefined,
      });
    }
  };

  const handleDelete = () => {
    if (!numLocId) return;
    if (confirm(`Delete location "${label}"?`)) {
      deleteLocMutation.mutate({ id: numLocId });
    }
  };

  const isSaving = createLocMutation.isPending || updateLocMutation.isPending;

  if (mode === 'edit' && isLocLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading location...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Top Sticky Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Link to={basePath}>
              <ArrowLeft className="h-4 w-4" />
              <span>Locations</span>
            </Link>
          </Button>

          <span className="text-muted-foreground/50">/</span>

          <h1 className="text-lg font-bold tracking-tight text-foreground truncate max-w-md">
            {mode === 'create'
              ? 'Add Depot / Location'
              : `Edit ${existingLoc?.label || 'Location'}`}
          </h1>
        </div>

        <div className="flex items-center gap-2.5">
          {mode === 'edit' && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10 border-destructive/30 gap-1.5"
              onClick={handleDelete}
              disabled={deleteLocMutation.isPending || isSaving}
            >
              {deleteLocMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              <span>Delete</span>
            </Button>
          )}

          <Button variant="outline" size="sm" asChild disabled={isSaving}>
            <Link to={basePath}>Cancel</Link>
          </Button>

          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSaving || !label.trim() || !formattedAddress.trim()}
            className="gap-1.5"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>{mode === 'create' ? 'Create Location' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>

      {/* Main Full Page Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-4xl w-full mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="h-4 w-4 text-primary" />
              <span>Depot Naming & Address</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Configure hubs, warehouses, or customer sites used as route starting points and
              destinations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Depot Label / Name *</label>
              <Input
                placeholder="e.g. Sydney Central Depot, Western Warehouse, South Hub"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Physical Street Address *
              </label>
              <AddressAutocomplete
                value={formattedAddress}
                onChange={setFormattedAddress}
                onSelectAddress={handleSelectAddress}
                initialLatitude={latitude}
                initialLongitude={longitude}
                placeholder="Start typing depot address or landmark..."
                required
              />
              <span className="text-[11px] text-muted-foreground">
                Used to compute Google Routes travel distances for operator morning departures and
                evening returns.
              </span>
            </div>

            {latitude != null && longitude != null && (
              <div className="flex items-center gap-4 text-xs text-muted-foreground p-2.5 rounded-lg bg-muted/40 border border-border/40 font-mono">
                <span>Lat: {latitude.toFixed(5)}</span>
                <span>Lng: {longitude.toFixed(5)}</span>
                {googlePlaceId && <span className="truncate">Place ID: {googlePlaceId}</span>}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">
                Access Instructions & Gate Codes
              </label>
              <Input
                placeholder="e.g. Gate 4, Entry code #4521, Key lockbox on north wall..."
                value={accessNotes}
                onChange={(e) => setAccessNotes(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-end gap-3 pt-4 pb-12 border-t border-border/40">
          <Button variant="outline" asChild disabled={isSaving}>
            <Link to={basePath}>Cancel</Link>
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !label.trim() || !formattedAddress.trim()}
            className="gap-2"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{mode === 'create' ? 'Create Location' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
